import {
  BaseService,
  BaseRequestMethod,
} from "src/app/core/base/services/base.service";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import {
  BaseSignMetadata,
  BaseSignMetadataValues,
  SimpleTag,
} from "../interfaces/document-sign.interface";
import { FormBuilder } from "@angular/forms";
import { catchError, concatMap, map, switchMap } from "rxjs/operators";
import { AppGlobals } from "src/app/app.globals";
import { AuthTokenService, AuthErrorMessage } from "src/app/core/base/services/auth-token.service";
@Injectable()
export class DocumentSignService extends BaseService {
  _formBuilder: FormBuilder;

  constructor(
    protected http: HttpClient,
    formBuilder: FormBuilder,
    private authTokenService: AuthTokenService
  ) {
    super(http);
    this._formBuilder = formBuilder;
  }

  public isTestEnvironment(): boolean {
    const host = (this._services?.backend?.host || "").toLowerCase();
    return host.includes("-test") || host.includes("localhost") || host.includes("127.0.0.1");
  }

  /**
   * Sottoscrizione agli errori di autenticazione per gestione UI
   */
  public get authErrors$(): Observable<AuthErrorMessage> {
    return this.authTokenService.authError$;
  }

  public getDocumentSignRequest(url: string): Observable<any> {
    return this.authTokenService.executeWithAuth((oauth_token) => {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/getDocument",
        { url: url },
        null,
        null,
        oauth_token
      );
    }).pipe(
      catchError((error) => {
        console.error("Error in getDocumentSignRequest:", error);
        this.logAuthError(url, 'getDocumentSignRequest', error);
        return throwError(error);
      })
    );
  }

  public sendOtpToSignDocumentRequest(
    url: string,
    tag: SimpleTag,
    language: string,
    signAll: boolean
  ): Observable<any> {
    return this.authTokenService.executeWithAuth((oauth_token) => {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/sendOTP",
        {
          url: url,
          tag: tag,
          language: language,
          signAll: signAll ?? null,
        },
        null,
        null,
        oauth_token
      );
    }).pipe(
      catchError((error) => {
        console.error("Error in sendOtpToSignDocumentRequest:", error);
        this.logAuthError(url, 'sendOtpToSignDocumentRequest', error);
        return throwError(error);
      })
    );
  }

  public verifyOtpToSignDocumentRequest(
    url: string,
    tag: SimpleTag,
    otpCode: string,
    signAll: boolean,
    oauth_token?: string
  ): Observable<any> {
    // Se viene passato un token, usalo direttamente
    if (oauth_token) {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/VerifyOTP",
        {
          url: url,
          tag: tag,
          otpCode: otpCode,
          signAll: signAll,
        },
        null,
        null,
        oauth_token
      ).pipe(
        catchError((error) => {
          // Se errore 401, prova con refresh automatico
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.authTokenService.handleUnauthorized((newToken) =>
              this.callService<any>(
                BaseRequestMethod.POST,
                "sign/VerifyOTP",
                { url, tag, otpCode, signAll },
                null,
                null,
                newToken
              )
            );
          }
          this.logAuthError(url, 'verifyOtpToSignDocumentRequest', error);
          return throwError(error);
        })
      );
    }

    // Altrimenti usa il flusso automatico
    return this.authTokenService.executeWithAuth((token) => {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/VerifyOTP",
        {
          url: url,
          tag: tag,
          otpCode: otpCode,
          signAll: signAll,
        },
        null,
        null,
        token
      );
    }).pipe(
      catchError((error) => {
        console.error("Error in verifyOtpToSignDocumentRequest:", error);
        this.logAuthError(url, 'verifyOtpToSignDocumentRequest', error);
        return throwError(error);
      })
    );
  }

  public applySignDocumentRequest(
    url: string,
    timezone: string,
    tags: Array<SimpleTag>,
    signMetadata: Array<BaseSignMetadata>,
    signMetadataValues: Array<BaseSignMetadataValues>,
    content: string,
    language: string,
    signAll: boolean,
    skipSummaryPage: boolean,
    oauth_token?: string
  ): Observable<any> {
    const requestData = {
      url: url,
      timezone: timezone,
      tags: tags,
      signMetadata: signMetadata,
      signMetadataValues: signMetadataValues,
      content: content,
      language: language,
      signAll: signAll,
      skipSummaryPage: skipSummaryPage,
    };

    // Se viene passato un token, usalo direttamente
    if (oauth_token) {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/apply",
        requestData,
        null,
        null,
        oauth_token
      ).pipe(
        catchError((error) => {
          // Se errore 401, prova con refresh automatico
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.authTokenService.handleUnauthorized((newToken) =>
              this.callService<any>(
                BaseRequestMethod.POST,
                "sign/apply",
                requestData,
                null,
                null,
                newToken
              )
            );
          }
          this.logAuthError(url, 'applySignDocumentRequest', error);
          return throwError(error);
        })
      );
    }

    // Altrimenti usa il flusso automatico
    return this.authTokenService.executeWithAuth((token) => {
      return this.callService<any>(
        BaseRequestMethod.POST,
        "sign/apply",
        requestData,
        null,
        null,
        token
      );
    }).pipe(
      catchError((error) => {
        console.error("Error in applySignDocumentRequest:", error);
        this.logAuthError(url, 'applySignDocumentRequest', error);
        return throwError(error);
      })
    );
  }

  /**
   * Ottiene un token OAuth con retry automatico e backoff esponenziale.
   * @deprecated Usa authTokenService.getValidToken() o authTokenService.executeWithAuth() invece
   */
  public getOAuthToken(): Observable<string | null> {
    return this.authTokenService.getValidToken().pipe(
      catchError((error) => {
        console.error("Error while retrieving access token:", error?.message);
        return throwError(new Error(`Unable to retrieve access token: ${error?.message}`));
      })
    );
  }

  /**
   * Invia un log di errore al backend
   */
  public sendErrorLog(
    url: string,
    method: string,
    step: string,
    exception: string,
    note: string | null
  ): Observable<any> {
    return this.callService<any>(
      BaseRequestMethod.POST,
      `sign/log/frontend?apikey=${this._services.backend.apikey}`,
      {
        url: url,
        method: method,
        step: step,
        exception: exception,
        note: note ?? null
      },
      null,
      null,
      null
    );
  }

  /**
   * Helper per loggare errori di autenticazione
   */
  private logAuthError(url: string, method: string, error: any): void {
    const errorMessage = error?.userFriendlyMessage || error?.message || 'Unknown error';
    const errorCode = error?.code || 'UNKNOWN';

    this.sendErrorLog(
      url,
      method,
      'authentication',
      `[${errorCode}] ${errorMessage}`,
      JSON.stringify({
        circuitState: this.authTokenService.getCircuitState(),
        canRetry: error?.canRetry ?? false
      })
    ).subscribe({
      error: (logError) => console.error('Failed to send error log:', logError)
    });
  }
}
